import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Add, Remove, VFlex, cn } from '@/package';
import { VIconButton } from '@/package/kiska/src/component/UI/VIconButton';
import type { OnboardingForm, Plan } from '@/type';

const Card = ({
  isSelected = false,
  title,
  subTitle,
  list,
  isCompleted = false,
}: {
  isSelected?: boolean;
  title: Plan['plan'];
  subTitle: string;
  list: string[];
  isCompleted?: boolean;
}) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<OnboardingForm>();

  const enterpriseTierStr = t('pricing.enterprise.anguillidae');

  const billingUnit =
    title === enterpriseTierStr ? watch('enterpriseBillingUnit') : 1;
  const isSubtractable = title === enterpriseTierStr && billingUnit >= 2;
  const isAddable = title === enterpriseTierStr;

  const onBillingUnitChange = (type: 'add' | 'subtract') => {
    if (type === 'add') {
      setValue('enterpriseBillingUnit', billingUnit + 1);
    } else {
      setValue('enterpriseBillingUnit', billingUnit - 1);
    }
  };

  return (
    <div
      className={cn(
        'h-[286px] w-[296px] cursor-pointer rounded-lg border p-6 text-zinc-900',
        {
          'border-gray-400': !isSelected,
          'bg-gray-100': !isSelected,
          'border-primary-400': isSelected,
          'bg-skyBlue-200': isSelected,
        },
      )}
    >
      <VFlex direction="col" className="gap-6">
        <VFlex direction="col" className="gap-1">
          <div className="text-headline-sm leading-6 font-bold ">{title}</div>
          {!isCompleted ? (
            <VFlex items="center" justify="between">
              <div className="text-body-md leading-none font-normal">
                {subTitle}
              </div>

              <VFlex justify="end">
                <VFlex className="gap-1 px-1" items="center">
                  <VIconButton
                    Icon={Remove}
                    size="md"
                    variant="roundSecondary"
                    onClick={() => onBillingUnitChange('subtract')}
                    disabled={!isSubtractable}
                  />
                  <VFlex
                    className="h-[24px] w-[29px]"
                    justify="center"
                    items="center"
                  >
                    <div>{billingUnit}</div>
                  </VFlex>
                  <VIconButton
                    Icon={Add}
                    size="md"
                    variant="roundSecondary"
                    onClick={() => onBillingUnitChange('add')}
                    disabled={!isAddable}
                  />
                </VFlex>
              </VFlex>
            </VFlex>
          ) : (
            <VFlex items="center" gap={0.5}>
              <div className="translate-y-[1px] text-body-md">
                {t('pricing.totalSeats.subintegumental')}
              </div>

              <div className="rounded-sm bg-white px-2 text-body-md font-bold">
                {billingUnit}
              </div>
            </VFlex>
          )}
        </VFlex>
        <VFlex
          direction="col"
          className="h-[108px] text-body-lg leading-none font-normal"
        >
          {list.map(item => (
            <div className="leading-normal" key={item}>
              • {item}
            </div>
          ))}
        </VFlex>
      </VFlex>
    </div>
  );
};

export default Card;
